/**
 * RTWO Asset & Token Engine — Supply Policy Service
 */

import type { AssetId, PolicyId, RTWOAssetSupplyPolicy } from '../domain-models';
import type { ISupplyPolicyService, SupplyPolicyResult } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type {
  LedgerAdapterRegistry,
  ScrtRuntimeAdapterRegistry,
} from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class SupplyPolicyService extends BaseASSService implements ISupplyPolicyService {
  readonly serviceId = 'ass-supply-policy-001';
  readonly serviceName = 'Supply Policy Service';
  readonly description =
    'Supply policy — placeholder, requires real Token Runtime. No fake token supplies. No fake minting. No fake burning.';
  readonly integrations = ['Core Engine', 'Ledger Engine', 'Smart Contract Runtime'];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = true;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry,
    private readonly scrtRegistry: ScrtRuntimeAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_token_runtime';
  }

  async configureSupplyPolicy(
    assetId: AssetId,
    supplyType: string,
    maxSupplyReference: string,
    mintingPolicy: string,
    burningPolicy: string
  ): Promise<SupplyPolicyResult> {
    if (!this.ledgerRegistry.isConfigured() || !this.scrtRegistry.isConfigured()) {
      return {
        success: false,
        policyId: null,
        error:
          'Supply policy configuration requires real Token Runtime — Token Runtime Not Operational',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Token Runtime Not Operational',
      };
    }

    const adapterResult = await this.scrtRegistry
      .getActiveAdapter()
      .configureSupplyPolicy(assetId, supplyType, maxSupplyReference, mintingPolicy, burningPolicy);

    if (adapterResult?.policy) {
      this.repositories.supplyPolicies.save(adapterResult.policy);
      this.context.eventBus.emit(ASSTInternalEventTypes.SUPPLY_POLICY_CONFIGURED, {
        policyId: adapterResult.policy.policyId,
        assetId,
      });
    }

    return {
      success: false,
      policyId: null,
      error:
        'Supply policy configuration requires real Token Runtime — Token Runtime Not Operational',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Token Runtime Not Operational',
    };
  }

  getAllPolicies(): RTWOAssetSupplyPolicy[] {
    if (!this.scrtRegistry.isConfigured()) return [];
    return this.repositories.supplyPolicies.findAll();
  }

  getPolicyByAssetId(assetId: AssetId): RTWOAssetSupplyPolicy | null {
    if (!this.scrtRegistry.isConfigured()) return null;
    return this.repositories.supplyPolicies.findByAssetId(assetId);
  }

  getPolicy(policyId: PolicyId): RTWOAssetSupplyPolicy | null {
    if (!this.scrtRegistry.isConfigured()) return null;
    return this.repositories.supplyPolicies.findById(policyId);
  }

  getActivePolicyCount(): 0 {
    return 0;
  }
}
